import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import Log from '@/Logger';

export interface InputField {
    label: string;
    name: string;
    type: 'email' | 'text' | 'password' | 'number' | 'url';
    required?: boolean;
    isAlphanumeric?: boolean; // 半角英数字チェックを行うフラグ
}

/**
 * ユーザーが入力したデータを収集し、`UserFormDialog` コンポーネントでフォームとして表示するカスタムフックです。
 * 
 * @param inputFields フォームで使用する入力フィールドの配列。
 * 各入力フィールドは `label`, `name`, `type`（'email', 'text', 'password', 'number', 'url'のいずれか）を含み、`required` はオプションで設定可能です。
 * @returns フォームデータ、フォーム送信のハンドラ、フォームの入力フィールドをレンダリングする関数を返します。
 * 
 * @example
 * const { formData, handleFormSubmit, renderInputFields } = useUserForm(inputFields);
 */
export function useUserForm(inputFields: InputField[]) {

    const [formData, setFormData] = React.useState<Record<string, string>>({});
    const [errors, setErrors] = React.useState<Record<string, string>>({});

    const validateInput = (value: string, isAlphanumeric?: boolean): boolean => {
        if (isAlphanumeric) {
            return /^[a-zA-Z0-9!@#$%^&*()\-=_+[\]{}|;:',.<>?\/]+$/.test(value);
        }
        return true; // isAlphanumericがfalseの場合、バリデーションをしない
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries(formData.entries());
        let valid = true;
        let validationErrors: Record<string, string> = {};

        // バリデーションチェック
        inputFields.forEach((field) => {
            const value = formJson[field.name] as string;
            if (field.required && !value) {
                validationErrors[field.name] = 'この項目は必須です';
                valid = false;
            } else if (field.isAlphanumeric && !validateInput(value, field.isAlphanumeric)) {
                validationErrors[field.name] = '半角英数字のみが有効です';
                valid = false;
            }
        });

        if (!valid) {
            setErrors(validationErrors);
            return;
        }

        setFormData(formJson as Record<string, string>);
        return formJson as Record<string, string>;
    };

    const renderInputFields = () => {
        return inputFields.map((field) => (
            <TextField
                key={field.name}
                autoFocus
                required={field.required}
                margin="dense"
                id={field.name}
                name={field.name}
                label={field.label}
                type={field.type}
                fullWidth
                variant="standard"
                error={!!errors[field.name]}
                helperText={errors[field.name]}
            />
        ));
    };

    return {
        formData,
        handleFormSubmit,
        renderInputFields,
    };
}

/**
 * ユーザー情報を入力するフォームをモーダルダイアログで表示するコンポーネントです。
 * フォーム送信またはキャンセルを処理するためのコールバックを受け取り、ユーザーの入力を管理します。
 * 
 * @param open ダイアログを開くかどうかを制御する状態。
 * @param onClose ダイアログが閉じられたときに呼び出されるコールバック。`'submit'` または `'cancel'` のアクションと、必要に応じてフォームデータを渡します。
 * @param inputFields フォームに表示する入力フィールドの配列。
 * @param title ダイアログのタイトル。
 * @param contentText ダイアログ内に表示するテキスト。
 * 
 * @returns JSX 要素を返し、ダイアログを表示します。
 * 
 * @example
 * <UserFormDialog
 *   open={isOpen}
 *   onClose={(action, data) => handleDialogClose(action, data)}
 *   inputFields={inputFields}
 *   title="ユーザー情報登録"
 *   contentText="<p>ここに説明文を入力</p>"
 * />
 */
export default function UserFormDialog({
    open,
    onClose,
    inputFields,
    title,
    contentText,
}: {
    open: boolean;
    onClose: (action: 'submit' | 'cancel', data?: Record<string, string>) => void;
    inputFields: InputField[];
    title: string;
    contentText: React.ReactNode | string;
}) {
    const logger = Log.getLogger();
    logger.ui.info('useUserForm', 'switchPageToSetting', `${inputFields.length} form is to render`)

    const { handleFormSubmit, renderInputFields } = useUserForm(inputFields);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        const data = handleFormSubmit(event);
        if (data) {
            onClose('submit', data);
            logger.ui.info('useUserForm', 'submitUserForm', `Form is submitted`)
        }
    };

    const handleCancel = () => {
        onClose('cancel');
        logger.ui.info('useUserForm', 'cancelUserForm', `Form is canceled`)
    };

    return (
        <Dialog open={open} onClose={() => onClose('cancel')} PaperProps={{ component: 'form', onSubmit: handleSubmit }}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {contentText}
                {renderInputFields()}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>キャンセル</Button>
                <Button type="submit">登録</Button>
            </DialogActions>
        </Dialog>
    );
}
